// Clients and their books.
//
// Each owner keeps a self-balancing set of books of their own. Rent collected for
// them is a liability, not brokerage income — so this page shows the client's
// operating statement, their budget against actuals, and their five-year outlook,
// none of which appear anywhere on the brokerage's own P&L.
//
// The statement is laid out the way an investor reads one: income, then operating
// expenses, then net operating income, and capital BELOW that line — because NOI
// before capital spending is the number they are actually judging.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Field, Modal, Input, Select, Textarea,
    Button, Notice, Loading, Empty, money, fmtDate, titleCase, statusTone,
} from '../../components/secure/pm/PMKit';
import PMAgreementsPanel from '../../components/secure/pm/PMAgreementsPanel';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEAR = new Date().getFullYear();

export default function PMOwnersPage() {
    const apiClient = useApiClient();
    const [params, setParams] = useSearchParams();
    const selectedId = params.get('owner');

    const [loading, setLoading] = useState(true);
    const [owners, setOwners] = useState([]);
    const [detail, setDetail] = useState(null);
    const [statement, setStatement] = useState(null);
    const [budget, setBudget] = useState(null);
    const [projection, setProjection] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [tab, setTab] = useState('overview');
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadList = useCallback(async () => {
        try {
            const [o, a] = await Promise.all([
                apiClient.get('/api/property-management/owners'),
                apiClient.get('/api/property-management/accounts'),
            ]);
            setOwners(o || []); setAccounts(a || []);
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    const loadDetail = useCallback(async (id) => {
        if (!id) { setDetail(null); return; }
        try {
            const d = await apiClient.get(`/api/property-management/owners/${id}`);
            setDetail(d);
            if (d.book) {
                const [s, b, p] = await Promise.all([
                    apiClient.get(`/api/property-management/books/${d.book.book_id}/statement?from=${YEAR}-01-01&to=${YEAR}-12-31`),
                    apiClient.get(`/api/property-management/books/${d.book.book_id}/budget/${YEAR}`),
                    apiClient.get(`/api/property-management/books/${d.book.book_id}/projection/${YEAR}`),
                ]);
                setStatement(s); setBudget(b); setProjection(p || []);
            }
        } catch (e) { setError({ message: e.message }); }
    }, [apiClient]);

    useEffect(() => { loadList(); }, [loadList]);
    useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

    const submit = async (path, body, method = 'post') => {
        setBusy(true); setError(null);
        try {
            await apiClient[method](path, body);
            setModal(null); setForm({});
            await loadList(); if (selectedId) await loadDetail(selectedId);
        } catch (e) {
            setError({ message: e.message, problems: e.problems });
        } finally { setBusy(false); }
    };

    // Budget against actual, by month. Twelve figures rather than an annual number
    // divided by twelve, because the tax bill and the summer turn do not fall evenly
    // and a flat twelfth hides variance exactly where it matters.
    const budgetRows = useMemo(() => {
        if (!budget) return [];
        const byAccount = {};
        for (const b of budget.budget || []) {
            byAccount[b.account_id] = byAccount[b.account_id] || {
                account_id: b.account_id, code: b.code, name: b.name,
                section: b.statement_section, budget: Array(12).fill(0), actual: Array(12).fill(0),
            };
            byAccount[b.account_id].budget[b.period_month - 1] = Number(b.amount);
        }
        for (const a of budget.actuals || []) {
            const acct = accounts.find((x) => x.account_id === a.account_id);
            byAccount[a.account_id] = byAccount[a.account_id] || {
                account_id: a.account_id, code: acct?.code, name: acct?.name,
                section: acct?.statement_section, budget: Array(12).fill(0), actual: Array(12).fill(0),
            };
            const isIncome = acct?.type === 'income';
            byAccount[a.account_id].actual[a.period_month - 1] =
                isIncome ? Number(a.credits) - Number(a.debits) : Number(a.debits) - Number(a.credits);
        }
        return Object.values(byAccount).sort((x, y) => String(x.code).localeCompare(String(y.code)));
    }, [budget, accounts]);

    if (loading) return <Loading />;

    const Section = ({ title, lines, total, strong }) => (
        <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">{title}</div>
            {(lines || []).length === 0 && <p className="text-sm text-slate-400 py-1">No activity.</p>}
            {(lines || []).map((l) => (
                <div key={l.code} className="flex justify-between py-1 text-sm border-b border-slate-50">
                    <span className="text-slate-600"><span className="text-slate-400 mr-2 tabular-nums">{l.code}</span>{l.name}</span>
                    <span className="tabular-nums text-slate-800">{money(l.amount)}</span>
                </div>
            ))}
            {total !== undefined && (
                <div className={`flex justify-between py-1.5 mt-1 border-t-2 border-slate-200 text-sm
                                 ${strong ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`}>
                    <span>{strong ? strong : `Total ${title.toLowerCase()}`}</span>
                    <span className="tabular-nums">{money(total)}</span>
                </div>
            )}
        </div>
    );

    return (
        <div>
            <PageHeader
                title="Clients &amp; Books"
                subtitle="Rent collected for an owner is their money held in trust, not brokerage income. Each client keeps their own self-balancing books."
            >
                <Button onClick={() => { setForm({ owner_kind: 'individual' }); setModal('owner'); }}>Add client</Button>
            </PageHeader>

            {error && <Notice tone="bad" message={error.message} problems={error.problems}
                onDismiss={() => setError(null)} />}

            <div className="grid lg:grid-cols-5 gap-5">
                <div className="lg:col-span-2">
                    <Panel title="Clients" subtitle={`${owners.length} on file`}>
                        <Table
                            columns={[
                                { key: 'legal_name', label: 'Client',
                                    render: (r) => (
                                        <div>
                                            <div className="font-medium text-slate-800">{r.legal_name}</div>
                                            <div className="text-xs text-slate-400">
                                                {r.book_no} · {r.property_count} propert{r.property_count === 1 ? 'y' : 'ies'}
                                            </div>
                                        </div>
                                    ) },
                                { key: 'w9', label: '', nowrap: true,
                                    render: (r) => r.w9_on_file
                                        ? <Pill tone="good">W-9</Pill> : <Pill tone="warn">no W-9</Pill> },
                            ]}
                            rows={owners.map((o) => ({ ...o, _key: o.owner_id }))}
                            onRowClick={(r) => setParams({ owner: r.owner_id })}
                            empty="No clients yet."
                        />
                    </Panel>
                </div>

                <div className="lg:col-span-3">
                    {!detail ? (
                        <Panel><Empty title="Select a client"
                            message="Their book, operating statement, budget and five-year outlook appear here." /></Panel>
                    ) : (
                        <>
                            <Panel title={detail.legal_name}
                                subtitle={`${titleCase(detail.owner_kind)} · book ${detail.book?.book_no || '—'}`}>
                                <div className="grid sm:grid-cols-4 gap-3">
                                    <Stat label="Properties" value={detail.properties.length} />
                                    <Stat label="Agreements" value={detail.agreements.length}
                                        tone={detail.agreements.some((a) => a.status === 'active') ? 'good' : 'warn'}
                                        sub={detail.agreements.some((a) => a.status === 'active') ? 'active' : 'none active'} />
                                    <Stat label="Reserve floor" value={money(detail.book?.reserve_floor)} />
                                    <Stat label="W-9" value={detail.w9_on_file ? 'On file' : 'Missing'}
                                        tone={detail.w9_on_file ? 'good' : 'warn'} sub="needed for the 1099" />
                                </div>
                            </Panel>

                            <div className="mt-5">
                                <Tabs
                                    tabs={[
                                        { key: 'overview', label: 'Overview' },
                                        { key: 'statement', label: 'Statement' },
                                        { key: 'budget', label: `Budget ${YEAR}` },
                                        { key: 'projection', label: 'Five-year', count: projection.length },
                                        { key: 'agreements', label: 'Agreements', count: detail.agreements.length },
                                    ]}
                                    active={tab} onChange={setTab}
                                />

                                {tab === 'overview' && (
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <Panel title="Contact">
                                            <dl>
                                                <Field label="Legal name" value={detail.legal_name} />
                                                <Field label="Contact" value={detail.contact_name} />
                                                <Field label="Email" value={detail.email} />
                                                <Field label="Phone" value={detail.phone} />
                                                <Field label="Address" value={[detail.street_address, detail.city,
                                                    detail.state, detail.zip_code].filter(Boolean).join(', ')} />
                                            </dl>
                                        </Panel>
                                        <Panel title="Distribution &amp; tax">
                                            <dl>
                                                <Field label="Remit by" value={titleCase(detail.remit_method)} />
                                                <Field label="Statements" value={titleCase(detail.statement_delivery)} />
                                                <Field label="TIN type" value={titleCase(detail.tin_type)} />
                                                <Field label="W-9 received" value={fmtDate(detail.w9_received_on)}
                                                    hint="Without it the 1099 cannot be filed correctly." />
                                            </dl>
                                        </Panel>
                                        <Panel title="Properties" className="sm:col-span-2">
                                            <Table
                                                columns={[
                                                    { key: 'name', label: 'Property' },
                                                    { key: 'city', label: 'City' },
                                                    { key: 'asset_class', label: 'Class', render: (r) => titleCase(r.asset_class) },
                                                    { key: 'total_units', label: 'Units', align: 'right' },
                                                    { key: 'ownership_percent', label: 'Share', align: 'right',
                                                        render: (r) => `${Number(r.ownership_percent).toFixed(2)}%` },
                                                    { key: 'status', label: 'Status', nowrap: true,
                                                        render: (r) => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                                                ]}
                                                rows={detail.properties.map((p) => ({ ...p, _key: p.pm_property_id }))}
                                                empty="No properties assigned to this client."
                                            />
                                        </Panel>
                                    </div>
                                )}

                                {tab === 'statement' && statement && (
                                    <Panel title={`Operating statement — ${YEAR}`}
                                        subtitle="Capital sits below net operating income, because NOI before capital is the figure an investor is judging.">
                                        <div className="grid sm:grid-cols-3 gap-3 mb-5">
                                            <Stat label="Income" value={money(statement.totals.income)} />
                                            <Stat label="Operating expense" value={money(statement.totals.operating_expense)} />
                                            <Stat label="Net operating income" value={money(statement.totals.net_operating_income)}
                                                tone={statement.totals.net_operating_income >= 0 ? 'good' : 'bad'} />
                                        </div>
                                        <Section title="Income" lines={statement.sections.income}
                                            total={statement.totals.income} />
                                        <Section title="Operating expenses" lines={statement.sections.operating_expense}
                                            total={statement.totals.operating_expense} />
                                        <div className="flex justify-between py-2 my-3 border-y-2 border-slate-800 font-bold text-slate-900">
                                            <span>Net operating income</span>
                                            <span className="tabular-nums">{money(statement.totals.net_operating_income)}</span>
                                        </div>
                                        <Section title="Capital" lines={statement.sections.capital}
                                            total={statement.totals.capital} />
                                        <div className="flex justify-between py-2 border-t-2 border-slate-300 font-bold text-slate-900">
                                            <span>Cash flow</span>
                                            <span className="tabular-nums">{money(statement.totals.cash_flow)}</span>
                                        </div>
                                    </Panel>
                                )}

                                {tab === 'budget' && (
                                    <Panel title={`Budget vs actual — ${YEAR}`}
                                        subtitle="Twelve figures per account, not an annual number divided by twelve."
                                        actions={<Button variant="ghost"
                                            onClick={() => { setForm({ period_month: 1 }); setModal('budget'); }}>
                                            Add budget line</Button>}>
                                        {budgetRows.length === 0
                                            ? <Empty title="No budget for this year"
                                                message="Add lines per account and month; actuals appear alongside as they post." />
                                            : (
                                                <div className="overflow-x-auto -mx-5 px-5">
                                                    <table className="text-xs min-w-max w-full">
                                                        <thead>
                                                            <tr className="border-b border-slate-200">
                                                                <th className="text-left py-2 pr-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold">Account</th>
                                                                {MONTHS.map((m) => (
                                                                    <th key={m} className="text-right py-2 px-1.5 text-[10px] uppercase text-slate-400 font-bold">{m}</th>
                                                                ))}
                                                                <th className="text-right py-2 pl-3 text-[10px] uppercase text-slate-400 font-bold">Year</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {budgetRows.map((r) => {
                                                                const bTot = r.budget.reduce((s, x) => s + x, 0);
                                                                const aTot = r.actual.reduce((s, x) => s + x, 0);
                                                                return (
                                                                    <React.Fragment key={r.account_id}>
                                                                        <tr className="border-b border-slate-50">
                                                                            <td className="py-1.5 pr-3 text-slate-700" rowSpan={2}>
                                                                                <span className="text-slate-400 mr-1.5 tabular-nums">{r.code}</span>{r.name}
                                                                            </td>
                                                                            {r.budget.map((v, i) => (
                                                                                <td key={i} className="text-right px-1.5 tabular-nums text-slate-400">
                                                                                    {v ? Math.round(v).toLocaleString() : '·'}
                                                                                </td>
                                                                            ))}
                                                                            <td className="text-right pl-3 tabular-nums text-slate-500">{Math.round(bTot).toLocaleString()}</td>
                                                                        </tr>
                                                                        <tr className="border-b border-slate-100">
                                                                            {r.actual.map((v, i) => {
                                                                                const over = r.budget[i] && v > r.budget[i] * 1.05;
                                                                                return (
                                                                                    <td key={i} className={`text-right px-1.5 tabular-nums font-medium
                                                                                        ${over ? 'text-rose-600' : 'text-slate-800'}`}>
                                                                                        {v ? Math.round(v).toLocaleString() : '·'}
                                                                                    </td>
                                                                                );
                                                                            })}
                                                                            <td className={`text-right pl-3 tabular-nums font-bold
                                                                                ${aTot > bTot * 1.05 && bTot ? 'text-rose-600' : 'text-slate-800'}`}>
                                                                                {Math.round(aTot).toLocaleString()}
                                                                            </td>
                                                                        </tr>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                    <p className="text-xs text-slate-400 mt-3">
                                                        Upper row budget, lower row actual. Actuals over 105% of budget show in red.
                                                    </p>
                                                </div>
                                            )}
                                    </Panel>
                                )}

                                {tab === 'projection' && (
                                    <Panel title="Five-year outlook"
                                        subtitle="A projection is not a budget. It carries stated assumptions and is expected to be wrong — which is why the assumption is required, not optional."
                                        actions={<Button variant="ghost"
                                            onClick={() => { setForm({ year_offset: 1 }); setModal('projection'); }}>
                                            Add projection</Button>}>
                                        <Table
                                            columns={[
                                                { key: 'year_offset', label: 'Year', nowrap: true,
                                                    render: (r) => `${Number(r.base_year) + Number(r.year_offset)}` },
                                                { key: 'summary_line', label: 'Line',
                                                    render: (r) => titleCase(r.summary_line || r.name) },
                                                { key: 'amount', label: 'Amount', align: 'right',
                                                    render: (r) => money(r.amount) },
                                                { key: 'growth_rate', label: 'Growth', align: 'right',
                                                    render: (r) => r.growth_rate !== null ? `${Number(r.growth_rate).toFixed(1)}%` : '—' },
                                                { key: 'assumption', label: 'Assumption' },
                                            ]}
                                            rows={projection.map((p) => ({ ...p, _key: p.projection_id }))}
                                            empty="No projection recorded. Every row must state the assumption behind it."
                                        />
                                    </Panel>
                                )}

                                {tab === 'agreements' && (
                                    <PMAgreementsPanel owner={detail} isBroker={!!getCurrentUser()?.isManagingBroker}
                                        onChanged={() => loadDetail(selectedId)} />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {modal === 'owner' && (
                <Modal title="Add a client" subtitle="A book opens with them — nothing can be posted without one."
                    onClose={() => setModal(null)} wide>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="Legal name" value={form.legal_name || ''}
                            onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
                        <Select label="Kind" value={form.owner_kind}
                            onChange={(e) => setForm({ ...form, owner_kind: e.target.value })}
                            options={['individual', 'joint', 'llc', 'corporation', 'partnership', 'trust', 'estate', 'other']} />
                        <Input label="Contact name" value={form.contact_name || ''}
                            onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                        <Input label="Email" type="email" value={form.email || ''}
                            onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <Input label="Phone" value={form.phone || ''}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        <Select label="TIN type" value={form.tin_type || 'unknown'}
                            onChange={(e) => setForm({ ...form, tin_type: e.target.value })}
                            options={['unknown', 'ssn', 'ein', 'itin']} />
                        <Input label="Street address" value={form.street_address || ''}
                            onChange={(e) => setForm({ ...form, street_address: e.target.value })} className="sm:col-span-2" />
                        <Input label="City" value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                        <Input label="ZIP" value={form.zip_code || ''} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} />
                        <Select label="Remit by" value={form.remit_method || 'check'}
                            onChange={(e) => setForm({ ...form, remit_method: e.target.value })}
                            options={['check', 'ach', 'wire', 'hold']} />
                        <Select label="Statements" value={form.statement_delivery || 'portal'}
                            onChange={(e) => setForm({ ...form, statement_delivery: e.target.value })}
                            options={['portal', 'email', 'mail']} />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy} onClick={() => submit('/api/property-management/owners', form)}>
                            {busy ? 'Saving…' : 'Add client'}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'budget' && (
                <Modal title={`Budget line — ${YEAR}`} onClose={() => setModal(null)}>
                    <Select label="Account" value={form.account_id || ''}
                        onChange={(e) => setForm({ ...form, account_id: e.target.value })}
                        options={[{ value: '', label: 'Select…' },
                            ...accounts.map((a) => ({ value: a.account_id, label: `${a.code} — ${a.name}` }))]} />
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Select label="Month" value={form.period_month}
                            onChange={(e) => setForm({ ...form, period_month: Number(e.target.value) })}
                            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} />
                        <Input label="Amount" type="number" step="0.01" value={form.amount || ''}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => submit(`/api/property-management/books/${detail.book.book_id}/budget/${YEAR}`,
                                { lines: [form] }, 'put')}>
                            {busy ? 'Saving…' : 'Save'}</Button>
                    </div>
                </Modal>
            )}

            {modal === 'projection' && (
                <Modal title="Five-year projection row"
                    subtitle="The assumption is required. A figure without the reasoning behind it reads as a promise rather than an estimate."
                    onClose={() => setModal(null)}>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Select label="Year" value={form.year_offset}
                            onChange={(e) => setForm({ ...form, year_offset: Number(e.target.value) })}
                            options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${YEAR + n} (year ${n})` }))} />
                        <Select label="Line" value={form.summary_line || ''}
                            onChange={(e) => setForm({ ...form, summary_line: e.target.value })}
                            options={[{ value: '', label: 'Select…' }, 'gross_potential_rent', 'vacancy_loss',
                                'effective_gross_income', 'operating_expenses', 'net_operating_income',
                                'capital_expenditure', 'debt_service', 'cash_flow', 'reserve_funding']} />
                        <Input label="Amount" type="number" step="0.01" value={form.amount || ''}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                        <Input label="Growth rate %" type="number" step="0.1" value={form.growth_rate ?? ''}
                            onChange={(e) => setForm({ ...form, growth_rate: e.target.value })} />
                    </div>
                    <Textarea label="Assumption (required)" value={form.assumption || ''}
                        onChange={(e) => setForm({ ...form, assumption: e.target.value })}
                        placeholder="e.g. 3% rent growth, 5% vacancy, tax reassessment in year 3" />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => submit(`/api/property-management/books/${detail.book.book_id}/projection/${YEAR}`,
                                { rows: [form] }, 'put')}>
                            {busy ? 'Saving…' : 'Save'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
